import { Action, AgentRunEvent, Document } from '@lucidtech/las-sdk-browser';
import { useMemo } from 'react';

import {
  CREATE_AGENT_RUN_FROM_EMAIL_FUNCTION_ID,
  EMAIL_CONTENT_TYPE,
  EXPORT_FUNCTION_IDS,
  REFETCH_AGENT_RUN_INTERVAL,
  TRIGGER_FUNCTION_IDS,
} from '@/constants';
import {
  toActionId,
  toAgentId,
  toAgentRunId,
  toDocumentId,
  useBatchGetActions,
  useBatchGetDocument,
  useGetAgent,
  useGetAgentRun,
} from '@/hooks/api';
import { isActionId, isDocumentId, isPredictionId, isUserId, isValidationId } from '@/utils';

const isTriggerEvent = (event: AgentRunEvent) => {
  return false;
};

const isPredictionEvent = (event: AgentRunEvent) => {
  return isPredictionId(event.resourceId);
};

const isReviewEvent = (event: AgentRunEvent) => {
  return isValidationId(event.validationId);
};

const isExportEvent = (event: AgentRunEvent, actions: Action[]) => {
  const action = actions.find((a) => a.actionId === event.actionId);
  return action ? EXPORT_FUNCTION_IDS.includes(action.functionId) : false;
};

const mergeEvents = (events: AgentRunEvent[], currentEvent: AgentRunEvent) => {
  const exists = !!events.find((e) => e.resourceId === currentEvent.resourceId);
  if (!exists) events.push(currentEvent);
  return events;
};

const createTriggerEvents = (
  taskDocument: Document | undefined,
  emailDocument: Document | undefined,
  actions: Action[]
) => {
  if (!taskDocument) return [];

  const event: AgentRunEvent = {
    resourceId: taskDocument.documentId,
    timestamp: taskDocument.createdTime,
    status: 'succeeded',
  };

  if (emailDocument) {
    event.actionId = actions.find((a) => a.functionId === CREATE_AGENT_RUN_FROM_EMAIL_FUNCTION_ID)?.actionId;
  } else if (isUserId(taskDocument.createdBy)) {
    event.trigger = 'manual';
    event.userId = taskDocument.createdBy;
  } else {
    const triggerActions = actions.filter(
      (a) => a.functionId !== CREATE_AGENT_RUN_FROM_EMAIL_FUNCTION_ID && TRIGGER_FUNCTION_IDS.includes(a.functionId)
    );
    if (triggerActions.length === 1) {
      event.actionId = triggerActions[0].actionId;
    } else {
      event.trigger = 'api';
    }
  }

  return [event];
};

export type UseAgentRunTreeOpts = {
  agentId?: string | null;
  runId?: string | null;
};

export const useAgentRunTree = ({ agentId, runId }: UseAgentRunTreeOpts) => {
  const { data: agent } = useGetAgent(toAgentId(agentId));
  const { data: run } = useGetAgentRun(toAgentRunId(agentId, runId), {
    fetchFromListCache: true,
    refetchInterval: REFETCH_AGENT_RUN_INTERVAL,
  });
  const { data: documents } = useBatchGetDocument(run?.resourceIds.filter(isDocumentId).map(toDocumentId));
  const { data: actions } = useBatchGetActions(agent?.resourceIds.filter(isActionId).map(toActionId));
  const emailDocument = useMemo(() => documents.find((d) => d.contentType === EMAIL_CONTENT_TYPE), [documents]);
  const taskDocument = useMemo(() => documents.find((d) => d.contentType !== EMAIL_CONTENT_TYPE), [documents]);

  const { triggerEvents, predictionEvents, reviewEvents, exportEvents } = useMemo(() => {
    let triggerEvents = createTriggerEvents(taskDocument, emailDocument, actions);
    let predictionEvents = [];
    let reviewEvents = [];
    let exportEvents = [];

    for (const event of (run?.events ?? []).toSorted((a, b) =>
      a.timestamp < b.timestamp ? -1 : a.timestamp > b.timestamp ? 1 : 0
    )) {
      if (isTriggerEvent(event)) triggerEvents.push(event);
      if (isPredictionEvent(event)) predictionEvents.push(event);
      if (isReviewEvent(event)) reviewEvents.push(event);
      if (isExportEvent(event, actions)) exportEvents.push(event);
    }

    triggerEvents = triggerEvents.reduceRight(mergeEvents, []);
    predictionEvents = predictionEvents.reduceRight(mergeEvents, []);
    reviewEvents = reviewEvents.reduceRight(mergeEvents, []);
    exportEvents = exportEvents.reduceRight(mergeEvents, []);

    triggerEvents.reverse();
    predictionEvents.reverse();
    reviewEvents.reverse();
    exportEvents.reverse();

    return { triggerEvents, predictionEvents, reviewEvents, exportEvents };
  }, [taskDocument, emailDocument, actions, run]);

  return {
    emailDocument,
    taskDocument,
    triggerEvents,
    predictionEvents,
    reviewEvents,
    exportEvents,
  };
};
